import React, { useEffect, useState } from 'react';
import { getUserHistory } from './services/historyLogger.js';
import { isComplete as isAudioCached } from './audioCache.js';

const UserHistoryPopup = ({ isOpen, onClose, onAudioSelect = () => {} }) => {
  const [historyData, setHistoryData] = useState(null); // grouped data
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch user history when popup opens
  useEffect(() => {
    if (!isOpen) return;

    const fetchHistory = async () => {
      setLoading(true);
      setError(null);
      try {
        const rawEvents = await getUserHistory(100); // fetch up to 100 events

        // Build a map of cacheKey -> cached? to avoid duplicate IndexedDB lookups
        const audioEvents = rawEvents.filter(
          (ev) => ev.type === 'audio_call' && ev.audioCall?.cacheKey
        );
        const uniqueKeys = Array.from(
          new Set(audioEvents.map((ev) => ev.audioCall.cacheKey))
        );

        // Query cache status for each unique key **once**
        const cacheStatuses = await Promise.all(
          uniqueKeys.map(async (key) => {
            try {
              return await isAudioCached(key);
            } catch (_) {
              return false;
            }
          })
        );
        const cacheMap = uniqueKeys.reduce((acc, key, idx) => {
          acc[key] = cacheStatuses[idx];
          return acc;
        }, {});

        // Attach cache status to every relevant event
        const eventsWithCache = rawEvents.map((ev) => {
          if (ev.type === 'audio_call' && ev.audioCall?.cacheKey) {
            return { ...ev, _isCached: cacheMap[ev.audioCall.cacheKey] || false };
          }
          return ev;
        });

        const grouped = groupEvents(eventsWithCache);
        setHistoryData(grouped);
      } catch (err) {
        console.error('[UserHistoryPopup] Failed to fetch history', err);
        setError('Could not load history');
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [isOpen]);

  // Group events by location/article hierarchy
  const groupEvents = (events) => {
    const locMap = new Map();

    events.forEach((ev) => {
      const locName = ev.location?.name || 'Unknown Location';
      let locEntry = locMap.get(locName);
      if (!locEntry) {
        locEntry = {
          name: locName,
          lastTimestamp: 0,
          audioCalls: [],
          articles: new Map(),
        };
        locMap.set(locName, locEntry);
      }

      // Determine JS timestamp for sorting
      let ts = 0;
      if (ev.timestamp?.toDate) {
        ts = ev.timestamp.toDate().getTime();
      } else if (ev.timestamp) {
        ts = new Date(ev.timestamp).getTime();
      }
      if (ts > locEntry.lastTimestamp) locEntry.lastTimestamp = ts;

      switch (ev.type) {
        case 'audio_call': {
          if (ev.article && ev.article.pageid) {
            // audio call about article
            const artKey = ev.article.pageid.toString();
            let artEntry = locEntry.articles.get(artKey);
            if (!artEntry) {
              artEntry = {
                article: ev.article,
                audioCalls: [],
                lastTimestamp: 0,
              };
              locEntry.articles.set(artKey, artEntry);
            }
            artEntry.audioCalls.push(ev);
            if (ts > artEntry.lastTimestamp) artEntry.lastTimestamp = ts;
          } else {
            // audio call directly about location
            locEntry.audioCalls.push(ev);
          }
          break;
        }
        case 'article_visit': {
          const artKey = ev.article.pageid.toString();
          let artEntry = locEntry.articles.get(artKey);
          if (!artEntry) {
            artEntry = {
              article: ev.article,
              audioCalls: [],
              lastTimestamp: 0,
            };
            locEntry.articles.set(artKey, artEntry);
          }
          if (ts > artEntry.lastTimestamp) artEntry.lastTimestamp = ts;
          break;
        }
        default:
          // location_visit already captured via timestamp
          break;
      }
    });

    // Convert to array sorted by recency
    const orderedLocations = Array.from(locMap.values()).sort(
      (a, b) => b.lastTimestamp - a.lastTimestamp
    );

    // Sort articles and audio calls within
    orderedLocations.forEach((loc) => {
      loc.audioCalls.sort((a, b) => {
        const tA = a.timestamp?.toDate ? a.timestamp.toDate().getTime() : 0;
        const tB = b.timestamp?.toDate ? b.timestamp.toDate().getTime() : 0;
        return tB - tA;
      });
      loc.articles = Array.from(loc.articles.values()).sort(
        (a, b) => b.lastTimestamp - a.lastTimestamp
      );
    });
    return orderedLocations;
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: '0',
        left: '0',
        width: '100vw',
        height: '100%',
        background: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        paddingTop: '20px',
        zIndex: 7001
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#333333',
          border: '1px solid #555555',
          borderRadius: '12px',
          padding: '16px',
          top: '10vh',
          width: 'clamp(300px, 80vw, 500px)',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 6px 25px rgba(0,0,0,0.3)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            position: 'sticky',
            top: 0,
            background: '#333333',
            zIndex: 1,
            paddingBottom: '10px',
          }}
        >
          <h2 style={{ margin: 0, color: '#f0f0f0', fontWeight: '600', fontSize: '16px' }}>
            User History
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#dddddd',
              fontSize: '28px',
              cursor: 'pointer'
            }}
            aria-label="Close user history"
          >
            ×
          </button>
        </div>

        <div style={{ overflowY: 'auto', flex: 1 }}>
          {loading && (
            <p style={{ color: '#cccccc', textAlign: 'center', marginTop: '20px' }}>Loading…</p>
          )}
          {error && (
            <p style={{ color: '#ff9999', textAlign: 'center', marginTop: '20px' }}>{error}</p>
          )}
          {!loading && !error && historyData?.length === 0 && (
            <p style={{ color: '#cccccc', textAlign: 'center', marginTop: '20px' }}>No history yet</p>
          )}
          {!loading && !error && historyData && historyData.length > 0 && (
            (() => {
              // Flatten all audio calls from all locations
              const allAudioCalls = historyData.flatMap(loc => [
                ...loc.audioCalls,
                ...loc.articles.flatMap((a) => a.audioCalls)
              ]);

              // Group by topic; keep one entry per mode, except anecdotes where multiple variants are kept.
              const topicGroups = {};
              allAudioCalls.forEach((call) => {
                const topic = call.audioCall?.topic;
                const mode = call.audioCall?.mode;
                if (!topic || !mode) return;

                if (!topicGroups[topic]) {
                  topicGroups[topic] = {};
                }

                const isAnecdote = mode === 'anecdote';
                const modeKey = isAnecdote
                  ? `anecdote:${call.audioCall?.cacheKey || call.id || Math.random().toString(36).slice(2, 8)}`
                  : mode;

                const current = topicGroups[topic][modeKey];

                // For standard modes keep newest; for anecdotes we keep unique entries by modeKey/cacheKey.
                if (!current ||
                    (call.timestamp?.toDate && current.timestamp?.toDate &&
                     call.timestamp.toDate() > current.timestamp.toDate())) {
                  topicGroups[topic][modeKey] = {
                    mode,
                    call,
                  };
                }
              });

              // Sort topics by most recent activity
              const sortedTopics = Object.entries(topicGroups).sort((a, b) => {
                const latestA = Math.max(...Object.values(a[1]).map(entry => 
                  entry.call?.timestamp?.toDate ? entry.call.timestamp.toDate().getTime() : 0
                ));
                const latestB = Math.max(...Object.values(b[1]).map(entry => 
                  entry.call?.timestamp?.toDate ? entry.call.timestamp.toDate().getTime() : 0
                ));
                return latestB - latestA;
              });

              return (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {sortedTopics.map(([topicName, modes]) => (
                    <li key={topicName} style={{ marginBottom: '16px' }}>
                      {/* Topic header */}
                      <div style={{ 
                        color: '#ffffff', 
                        fontWeight: '600', 
                        marginBottom: '8px', 
                        fontSize: '16px',
                        cursor: 'pointer',
                        padding: '8px 0'
                      }}>
                        {topicName}
                      </div>
                      
                      {/* Mode subitems */}
                      <ul style={{ listStyle: 'none', paddingLeft: '16px', paddingRight: '16px', margin: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {Object.entries(modes).map(([modeKey, entry]) => {
                          const mode = entry?.mode;
                          const call = entry?.call;

                          const getModeDisplay = (mode) => {
                            switch (mode) {
                              case 'overview': 
                                return {
                                  label: 'Overview',
                                  icon: <i className="fas fa-volume-up" style={{ fontSize: '14px' }}></i>
                                };
                              case 'historyOverview': 
                                return {
                                  label: 'History',
                                  icon: (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                                      <i className="fas fa-landmark" style={{ fontSize: '12px' }}></i>
                                      <i className="fas fa-volume-up" style={{ fontSize: '12px' }}></i>
                                    </div>
                                  )
                                };
                              case 'anecdote':
                                return {
                                  label: 'Anecdote',
                                  icon: (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                                      <i className="fas fa-face-laugh" style={{ fontSize: '12px' }}></i>
                                      <i className="fas fa-volume-up" style={{ fontSize: '12px' }}></i>
                                    </div>
                                  )
                                };
                              case 'historicPeriod': 
                                return {
                                  label: 'Historic Period',
                                  icon: (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                                      <i className="fas fa-clock" style={{ fontSize: '12px' }}></i>
                                      <i className="fas fa-volume-up" style={{ fontSize: '12px' }}></i>
                                    </div>
                                  )
                                };
                              default: 
                                return {
                                  label: mode,
                                  icon: <i className="fas fa-volume-up" style={{ fontSize: '14px' }}></i>
                                };
                            }
                          };

                          const modeDisplay = getModeDisplay(mode);

                          return (
                            <li
                              key={modeKey}
                              onClick={() => onAudioSelect(call)}
                              style={{
                                background: 'rgba(255,255,255,0.08)',
                                padding: '8px 12px',
                                borderRadius: '6px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                cursor: 'pointer',
                                color: '#e0e0e0',
                                fontSize: '13px',
                                border: '1px solid rgba(255,255,255,0.1)',
                                transition: 'background 0.2s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.background = 'rgba(255,255,255,0.12)';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.background = 'rgba(255,255,255,0.08)';
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {modeDisplay.icon}
                                <span>{modeDisplay.label}</span>
                              </div>
                              {call._isCached && (
                                <i className="fas fa-save" title="Cached" style={{ color: '#7dd07d', fontSize: '14px' }}></i>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </li>
                  ))}
                </ul>
              );
            })()
          )}
        </div>
      </div>
    </div>
  );
};

export default UserHistoryPopup; 